// The pull request page's loads as a runtime Program (runtime/effects.ts):
// its state, the messages that change it, and `update`, which gives the
// next state and the effects to run. Every read runs in a lane of the
// Program's runner, so an answer arrives only while its run is the lane's
// newest, and nothing here compares generations:
//
// - `pr`: the pull request itself (metadata, threads, timeline, checks). A
//   load supersedes the one in flight: the newest read is the one shown.
//   While one runs, the world may say how far it is (`pr-progress`), and
//   likewise for the files (`files-progress`): what a loading screen shows.
// - `checks`: the head's checks alone, after a check notice.
// - `files`: the changed files, one read per head (a call for the head
//   being read joins it; one for another head supersedes it).
// - `drafts`, `overrides`, `attention`: the viewer's drafts and the
//   attention overrides from the changelist server, and the attention set
//   worked out after each read of the overrides.
// - `org`, `refs`, `deployments`, `annotations`, `access`, `generated`,
//   `replaced`, `marks`, `viewed`: what the page shows beside the pull
//   request (`viewed`: the further pages of GitHub's "Viewed" states).
// - `stack`: what the pull request's stack is found among, asked for with
//   the page and apart from the pull request: first what the app already
//   knows (another member's page, the dashboard's saved rows), which costs
//   nothing and is there before the pull request is; then, when that is
//   nothing or older than STACK_STALE_MS, one background request. It is
//   read once per page; a later load of the pull request reads it again
//   only when the pull request's own branches or state changed, when it
//   is older than the bound, or after a rate limit. Its failure is its own
//   (the page shows everything else).
// - `refresh`, `checks soon`, `resume`: timers. `resume` is the runtime's
//   `retry`: the refetch owed when a rate limit lifts, which the load's
//   failure shows as its retry only while it is armed.
// - `write <n>`: each write the store makes (a reply, a resolve, a
//   publish, a draft), in a lane of its own so nothing supersedes it. It
//   is recorded in the account's invalidation log as an intent before it
//   is sent and done once answered, and its answer goes back to the
//   store's pending writes (`out`).
//
// Two reads of one thing are ordered by their basis, the count of reads
// started when each began (`reads`): checks refreshed after a load began
// outlast that load's answer, and a load begun after a checks refresh
// outlasts the refresh. A write the server accepted is confirmed by the
// first load whose basis is later than its acceptance (`out`: the store
// settles its pending writes in the step that shows the load).
//
// `server` is the pull request as GitHub and the changelist server last
// sent it: loads write it here, and so do the answers to the store's own
// writes (a draft created, drafts published), as messages.
import { isNotInstalled } from '../../api/errors';
import { failureOf, type Failure } from '../../api/failure';
import type { Coverage } from '../../api/install';
import type { CheckContext, PrFile, RepositoryData } from '../../api/queries';
import { RateLimitError } from '../../api/rateLimit';
import type { PrWriteKind } from '../../runtime/appLog';
import { PERSONAL_POLICY, type OrgPolicy, type ReviewDraft, type ServerOverride } from '../../api/server';
import type { AttentionMember } from '../../engine/types';
import type { KnownRef } from '../../logic/links';
import type { DeploymentCommit } from '../../logic/previews';
import { forcePushBefores, type ReplacedLookup } from '../../logic/range';
import { after, cancel, retry, Scheduled, task, type Effect, type Next, type Outcome } from '../../runtime/effects';
import { failed, has, loading, manual, notAsked, refreshFailed, running, settle, stale, staleSince, type Remote, type Retry } from '../../runtime/remote';
import { EMPTY_PR_STATE, type PrState } from '../optimistic';
import type { Titles } from '../related';
import type { RemoteViewed } from '../viewedSync.svelte';
import type { StackPool } from '../../logic/prStack';
import { SNAPSHOT_STALE_MS } from '../dashCache';
import { message, type CheckAnnotation, type FreshChecks, type KeptStack, type LoadedFiles, type LoadedPr, type PrProgress } from './fetch';

/** Delay before a refetch after a notice, reconnect or focus, so a burst costs one. */
export const REFRESH_DELAY_MS = 250;
/**
 * Minimum spacing of checks refetches. A CI run sends several check events
 * per job, and each arrives on both the PR and the repository topic.
 */
export const CHECKS_COALESCE_MS = 1000;
/** Random delay added after a rate-limit pause, so tabs do not refetch at once. */
export const RESUME_JITTER_MS = 2000;

/**
 * How old a stack read may be before GitHub is asked again: the bound past
 * which the dashboard checks its own saved copy, whose rows a stack may
 * come from.
 */
export const STACK_STALE_MS = SNAPSHOT_STALE_MS;

/** A read answered from what was kept: nothing changes. */
export const KEPT = { kind: 'kept' } as const;
export type Kept = typeof KEPT;

/** What the Program does through the world. Reads stop (or their answers are dropped) when `signal` aborts. */
export interface PrWorld {
  pr(signal: AbortSignal): Promise<LoadedPr | null>;
  checks(signal: AbortSignal): Promise<FreshChecks | null>;
  /** The changed files and the head GitHub listed them at. */
  files(signal: AbortSignal): Promise<LoadedFiles>;
  drafts(signal: AbortSignal): Promise<ReviewDraft[]>;
  overrides(signal: AbortSignal): Promise<ServerOverride[]>;
  /** The attention set of what the server sent; null when there is no pull request to work it out for. */
  attention(server: PrState, truncated: boolean): Promise<AttentionMember[] | null>;
  org(signal: AbortSignal): Promise<OrgPolicy>;
  /** Whether the App covers the repository; `unknown` when it cannot be told. */
  access(repository: RepositoryData, signal: AbortSignal): Promise<Coverage>;
  /** The related issues' titles, or `KEPT` when those read lately still stand. */
  refs(signal: AbortSignal): Promise<Titles | Kept>;
  /** Deployments of the latest commits at `head`, or `KEPT`. */
  deployments(head: string | null, signal: AbortSignal): Promise<DeploymentCommit[] | Kept>;
  replaced(base: string, befores: string[], signal: AbortSignal): Promise<ReplacedLookup>;
  annotations(run: CheckContext, signal: AbortSignal): Promise<CheckAnnotation[]>;
  generated(head: string, signal: AbortSignal): Promise<(path: string) => boolean>;
  /** What the app already knows of the pull request's stack, at no request (see `keptStack`). */
  stackKept(): Promise<KeptStack>;
  /** What GitHub says the stack is found among (see `fetchStack`); null when it does not show the pull request. */
  stackFetch(signal: AbortSignal): Promise<StackPool | null>;
  /** Read the review marks again. */
  marks(): Promise<void>;
  /**
   * Record in the account's invalidation log that a write of `kind` is
   * about to be sent to the pull request; the returned function records it
   * done (answered, or failed: it may have gone through).
   */
  writing(kind: PrWriteKind): () => void;
  /** Read GitHub's "Viewed" states after cursor `after` at `head`, handing each page over as it arrives; true when every page was read. */
  viewedPages(after: string, head: string, signal: AbortSignal): Promise<boolean>;
  /** Until when GitHub calls are paused (epoch ms), or null. */
  pausedUntil(): number | null;
  now(): number;
  random(): number;
}

export type { LoadedFiles };

/** What the store does in the step a state arrives (see the file comment). */
export type Out =
  /** A read of `resource` begun at `basis` was stored: writes accepted before it are confirmed. */
  | { kind: 'settle'; resource: 'pr' | 'overrides'; basis: number }
  /** A read of `resource` begun at `basis` failed: writes accepted before it stay shown, no longer in flight. */
  | { kind: 'stall'; resource: 'pr' | 'overrides'; basis: number }
  /** What the changelist server kept for the repository is dropped, and never read again. */
  | { kind: 'forget' }
  /** Write number `write` was answered with `o` (accepted, or failed): `settle` is told. */
  | { kind: 'answered'; write: number; o: Outcome<unknown>; settle: (o: Outcome<unknown>) => void }
  /** GitHub's "Viewed" states came with a load. */
  | { kind: 'viewed'; viewed: RemoteViewed };

/** Files that follow a load: always, only when some are loaded by then, or not. */
type Follow = 'files' | 'if-loaded' | null;

/** A confirmation owed once the attention set is worked out from a read of the overrides. */
interface Owed {
  basis: number;
  ok: boolean;
}

interface AnnotationRun {
  completedAt: string | null;
  list: CheckAnnotation[];
}

export interface PrLoads {
  /** The pull request as GitHub and the changelist server last sent it. */
  readonly server: PrState;
  readonly viewer: string;
  readonly repository: RepositoryData | null;
  /** Older timeline items were dropped (more than the page cap). */
  readonly timelineTruncated: boolean;
  /** The viewer lost access to the repository: nothing of it is shown, and nothing loads. */
  readonly revoked: boolean;
  /** The changelist server knows no installation covering the repository: it keeps no data for it. */
  readonly unrecorded: boolean;
  /** Whether the App can write to the repository for the viewer. */
  readonly access: Coverage;

  /** The pull request's load: its last answer (null: GitHub does not show it). */
  readonly pr: Remote<LoadedPr | null>;
  /** Reads of the pull request and of its checks started so far: each one's basis. */
  readonly reads: number;
  /** The load in flight. */
  readonly prRun: { readonly basis: number; readonly follow: Follow } | null;
  /** How far the load in flight is past its first answer; null before that, and with no load in flight. */
  readonly prProgress: PrProgress | null;
  /** Loads begun up to this basis have answered, or were superseded by one that has. */
  readonly prDone: number;
  /** The basis of the load on screen, and of the checks on screen when a refresh read them. */
  readonly shownBasis: number;
  readonly checksBasis: number;
  /** When the load on screen answered (epoch ms; 0: never). */
  readonly lastLoadAt: number;

  readonly checksRun: { readonly basis: number } | null;
  readonly checksDone: number;
  /** The coalescing timer is armed; a refresh was asked for while one ran. */
  readonly checksArmed: boolean;
  readonly checksAgain: boolean;

  readonly files: Remote<LoadedFiles>;
  readonly filesRun: { readonly head: string | null } | null;
  /** Files the listing in flight holds so far; null before its first page, and with none in flight. */
  readonly filesListed: number | null;

  /** The viewer's drafts as the server last listed them (writes land in `server.drafts`). */
  readonly drafts: Remote<ReviewDraft[]>;
  readonly draftsRun: boolean;
  /** A write changed the drafts while they were being read: that read is run again, not shown. */
  readonly draftsStale: boolean;
  /** Drafts dropped so far, and when (by that count) each was last dropped: a late answer never brings one back. */
  readonly drops: number;
  readonly droppedAt: ReadonlyMap<ReviewDraft['id'], number>;
  /**
   * How many answers to draft writes (a draft saved, edited, ticked,
   * restored or dropped) have reached the drafts. A whole list of drafts
   * that began being read before one of them (the mirror's answer) may
   * predate it, and is not shown.
   */
  readonly draftWrites: number;

  readonly overrides: Remote<ServerOverride[]>;
  /** Reads of the overrides started; those begun up to `overridesDone` are answered and their attention worked out. */
  readonly overridesReads: number;
  readonly overridesDone: number;
  /** The attention runs started and delivered, and what the one in flight owes. */
  readonly attentionRuns: number;
  readonly attentionDone: number;
  readonly attentionOwed: readonly Owed[];

  readonly org: Remote<OrgPolicy>;
  readonly orgRun: boolean;
  /** Titles of the related issues and pull requests. */
  readonly refs: Remote<KnownRef[]>;
  readonly refsRun: boolean;
  /** The server cannot answer the titles query: never asked again. */
  readonly refsFinal: boolean;
  readonly deployments: Remote<DeploymentCommit[]>;
  readonly deploymentsRun: boolean;
  /**
   * What the pull request's stack is found among (logic/prStack.ts): stale
   * while a read older than STACK_STALE_MS is checked with GitHub, and
   * after that check failed. Null when the pull request is known to be on
   * its own, or GitHub does not show it.
   */
  readonly stack: Remote<StackPool | null>;
  /** The stack was asked for (once per page), and a read of it is in flight. */
  readonly stackAsked: boolean;
  readonly stackRun: boolean;
  /** When what `stack` holds was read (epoch ms; 0: nothing read). */
  readonly stackAt: number;
  /** The pull request's own base, head and state as last loaded: a change in them may change its stack. Null before its first load. */
  readonly stackOwn: string | null;
  /** Commits head force-pushes replaced, and the head and pushes they were looked up for ('' when none). */
  readonly replaced: ReplacedLookup | null;
  readonly replacedKey: string;
  /** Annotations by file, and by check run as fetched (a completed run's do not change). */
  readonly annotations: Readonly<Record<string, CheckAnnotation[]>>;
  readonly annotationRuns: ReadonlyMap<number, AnnotationRun>;
  readonly annotationsRun: boolean;
  readonly annotationsAgain: boolean;
  readonly generated: (path: string) => boolean;
  /** What is read once per page (marks, drafts, .gitattributes) was asked for. */
  readonly sideLoaded: boolean;
  readonly accessRuns: number;
  readonly accessDone: number;

  /** Writes sent so far (each one's number), and how many are not answered yet. */
  readonly writes: number;
  readonly sending: number;

  /** The head whose further pages of "Viewed" states were read (or are being read). */
  readonly viewedPagesHead: string | null;

  /** The refetch armed for when a rate limit lifts, and whether the files follow it. */
  readonly resume: Scheduled | 'arming' | null;
  readonly resumeFiles: boolean;

  /** What the store does on receiving this state; a new list each time there is something. */
  readonly out: readonly Out[];
}

export type Msg =
  | { t: 'load'; follow?: Follow }
  | { t: 'pr'; basis: number; o: Outcome<LoadedPr | null> }
  /** The load in flight says how far it is. */
  | { t: 'pr-progress'; progress: PrProgress }
  | { t: 'refresh'; files: boolean }
  | { t: 'refresh-fire'; files: boolean }
  | { t: 'files'; force: boolean }
  | { t: 'files-loaded'; o: Outcome<LoadedFiles> }
  /** The listing in flight holds `listed` files so far. */
  | { t: 'files-progress'; listed: number }
  | { t: 'drafts' }
  | { t: 'drafts-loaded'; o: Outcome<ReviewDraft[]> }
  /** The server's answer for one draft, to a write begun when `drops` was `since`. */
  | { t: 'draft-landed'; draft: ReviewDraft; since: number }
  | { t: 'drafts-dropped'; ids: readonly ReviewDraft['id'][] }
  /** The drafts as the server held them once it had mirrored the viewer's pending review on GitHub, asked when `draftWrites` was `since`. */
  | { t: 'drafts-mirrored'; drafts: ReviewDraft[]; since: number }
  | { t: 'overrides' }
  | { t: 'overrides-loaded'; basis: number; o: Outcome<ServerOverride[]> }
  | { t: 'attention' }
  | { t: 'attention-done'; run: number; o: Outcome<AttentionMember[] | null> }
  | { t: 'checks' }
  | { t: 'checks-loaded'; basis: number; o: Outcome<FreshChecks | null> }
  | { t: 'checks-soon' }
  | { t: 'checks-fire' }
  | { t: 'org-loaded'; o: Outcome<OrgPolicy> }
  | { t: 'refs-loaded'; o: Outcome<Titles | Kept> }
  | { t: 'deployments-loaded'; o: Outcome<DeploymentCommit[] | Kept> }
  /** Read the stack from GitHub (its Try again). */
  | { t: 'stack' }
  | { t: 'stack-kept'; o: Outcome<KeptStack> }
  | { t: 'stack-loaded'; o: Outcome<StackPool | null> }
  | { t: 'replaced' }
  | { t: 'replaced-loaded'; key: string; o: Outcome<ReplacedLookup> }
  | { t: 'annotations' }
  | { t: 'annotations-loaded'; runs: CheckContext[]; got: [number, AnnotationRun | null][] }
  | { t: 'access' }
  | { t: 'access-checked'; run: number; o: Outcome<Coverage> }
  | { t: 'generated'; o: Outcome<(path: string) => boolean> }
  | { t: 'viewed-pages'; head: string; complete: boolean }
  /** A refetch is owed once the rate-limit pause ends (at `until` at the latest it said). */
  | { t: 'paused'; files: boolean; until: number | null }
  | { t: 'resume-armed'; s: Scheduled }
  | { t: 'resume-fire'; s: Scheduled }
  | { t: 'resume-lapsed'; s: Scheduled }
  /** Send a write of `kind`; `settle` is told its answer (the store's pending writes wait on it). */
  | { t: 'write'; kind: PrWriteKind; send: () => Promise<unknown>; settle: (o: Outcome<unknown>) => void }
  | { t: 'write-answered'; write: number; o: Outcome<unknown>; settle: (o: Outcome<unknown>) => void }
  | { t: 'not-installed' }
  | { t: 'revoked' }
  /** The store (or a test) puts these in place of what is held. */
  | { t: 'set'; patch: Partial<Pick<PrLoads, 'server' | 'viewer' | 'repository' | 'access' | 'unrecorded'>> }
  | { t: 'done' };

const NO_OUT: readonly Out[] = Object.freeze([]);

export function initialState(): PrLoads {
  return {
    server: EMPTY_PR_STATE,
    viewer: '',
    repository: null,
    timelineTruncated: false,
    revoked: false,
    unrecorded: false,
    access: 'unknown',
    pr: notAsked,
    reads: 0,
    prRun: null,
    prProgress: null,
    prDone: 0,
    shownBasis: 0,
    checksBasis: 0,
    lastLoadAt: 0,
    checksRun: null,
    checksDone: 0,
    checksArmed: false,
    checksAgain: false,
    files: notAsked,
    filesRun: null,
    filesListed: null,
    drafts: notAsked,
    draftsRun: false,
    draftsStale: false,
    drops: 0,
    droppedAt: new Map(),
    draftWrites: 0,
    overrides: notAsked,
    overridesReads: 0,
    overridesDone: 0,
    attentionRuns: 0,
    attentionDone: 0,
    attentionOwed: [],
    org: notAsked,
    orgRun: false,
    refs: notAsked,
    refsRun: false,
    refsFinal: false,
    deployments: notAsked,
    deploymentsRun: false,
    stack: notAsked,
    stackAsked: false,
    stackRun: false,
    stackAt: 0,
    stackOwn: null,
    replaced: null,
    replacedKey: '',
    annotations: {},
    annotationRuns: new Map(),
    annotationsRun: false,
    annotationsAgain: false,
    generated: () => false,
    sideLoaded: false,
    accessRuns: 0,
    accessDone: 0,
    writes: 0,
    sending: 0,
    viewedPagesHead: null,
    resume: null,
    resumeFiles: false,
    out: NO_OUT,
  };
}

/**
 * Whether the changelist server keeps data for the repository (marks,
 * review drafts, attention overrides): not once the viewer lost access,
 * nor where no installation of the App covers it.
 */
export const serverData = (s: PrLoads): boolean => !s.revoked && s.access !== 'not_installed' && !s.unrecorded;

/** The server's own data is never read again (see `not-installed`, `revoked`). */
const serverOff = (s: PrLoads): boolean => s.revoked || s.unrecorded;

/** Why a remote's load, or the refresh of what it shows, failed; null when it did not. */
export function failureIn(r: Remote<unknown>): { reason: Failure; retry: Retry } | null {
  if (r.tag === 'Failed') return r;
  if (r.tag === 'Has' && r.freshness.kind === 'stale' && r.freshness.refresh.kind === 'failed') return r.freshness.refresh;
  return null;
}

/** `r` once reading it again began: what it shows stays, a failure gives way to the read. */
function reading<T>(r: Remote<T>, shows: (v: T) => boolean = () => true): Remote<T> {
  if (r.tag !== 'Has' || !shows(r.value)) return loading;
  const f = r.freshness;
  return f.kind === 'stale' ? has(r.value, staleSince(f.since, running), r.gaps) : r;
}

/** `r` once reading it again failed for `reason`: what it showed stays, as of `since`. */
function unread<T>(r: Remote<T>, reason: Failure, retryBy: Retry, since: number, shows: (v: T) => boolean = () => true): Remote<T> {
  if (r.tag !== 'Has' || !shows(r.value)) return failed(reason, retryBy);
  return stale(r.value, r.freshness.kind === 'stale' ? r.freshness.since : since, refreshFailed(reason, retryBy));
}

/** `r` with a rate-limited failure's manual retry taken over by `sc`, the refetch armed for the limit's end. */
function adopt<T>(r: Remote<T>, sc: Scheduled): Remote<T> {
  const f = failureIn(r);
  if (!f || f.retry.kind !== 'manual' || f.reason.cause !== 'rate_limited') return r;
  if (r.tag === 'Failed') return failed(r.reason, sc);
  if (r.tag === 'Has' && r.freshness.kind === 'stale') return has(r.value, staleSince(r.freshness.since, refreshFailed(f.reason, sc)), r.gaps);
  return r;
}

const shown = (v: LoadedPr | null) => v !== null;

const completedRuns = (s: PrLoads): CheckContext[] =>
  (s.server.pr?.commits.nodes[0]?.commit.statusCheckRollup?.contexts.nodes ?? []).filter(
    (c) => c.__typename === 'CheckRun' && !!c.databaseId && c.status === 'COMPLETED',
  );

/** Annotations by file, for `runs`, from what was fetched for each. */
function byFile(runs: CheckContext[], fetched: ReadonlyMap<number, AnnotationRun>): Record<string, CheckAnnotation[]> {
  const out: Record<string, CheckAnnotation[]> = {};
  for (const run of runs) for (const a of fetched.get(run.databaseId!)?.list ?? []) (out[a.path] ??= []).push(a);
  return out;
}

/** How two of the server's times order (RFC 3339 in UTC, any number of fraction digits): negative when `a` is earlier. */
export function stampOrder(a: string, b: string): number {
  const key = (t: string) => {
    const m = /^(.*?)(?:\.(\d+))?Z$/.exec(t);
    return m ? `${m[1]}.${(m[2] ?? '').padEnd(9, '0')}` : t;
  };
  const [x, y] = [key(a), key(b)];
  return x < y ? -1 : x > y ? 1 : 0;
}

type Fx = Effect<Msg>[];

/** The Program's `update` over `world`. */
export function makeUpdate(world: PrWorld): (s: PrLoads, m: Msg) => Next<PrLoads, Msg> {
  /** The retry a failure is shown with: the armed refetch for a rate limit, while it is pending; else the user's. */
  const retryFor = (s: PrLoads, f: Failure): Retry =>
    f.cause === 'rate_limited' && s.resume instanceof Scheduled && s.resume.pending ? s.resume : manual;

  /** Arm the refetch for when the rate limit lifts, unless one is armed; `files` follow it if any call asked. */
  const pause = (s: PrLoads, fx: Fx, files: boolean, until: number | null): PrLoads => {
    if (s.revoked) return s;
    if (s.resume !== null) return { ...s, resumeFiles: s.resumeFiles || files };
    const now = world.now();
    const end = Math.max(world.pausedUntil() ?? -Infinity, until ?? -Infinity);
    const at = end > now ? end + world.random() * RESUME_JITTER_MS : now;
    fx.push(
      retry<Msg>('resume', at, {
        armed: (sc) => ({ t: 'resume-armed', s: sc }),
        fire: (sc) => ({ t: 'resume-fire', s: sc }),
        lapsed: (sc) => ({ t: 'resume-lapsed', s: sc }),
      }),
    );
    return { ...s, resume: 'arming', resumeFiles: files };
  };

  const startLoad = (s: PrLoads, fx: Fx, follow: Follow): PrLoads => {
    if (s.revoked) return s;
    const basis = s.reads + 1;
    fx.push(task('pr', (signal) => world.pr(signal), (o) => ({ t: 'pr', basis, o })));
    return { ...s, reads: basis, prRun: { basis, follow }, prProgress: null, pr: reading(s.pr, shown) };
  };

  const startFiles = (s: PrLoads, fx: Fx, force: boolean): PrLoads => {
    if (s.revoked) return s;
    const head = s.server.pr?.headRefOid ?? null;
    if (!force && s.files.tag === 'Has' && s.files.value.head === head) return s;
    // A read for this head is under way: the caller waits on it.
    if (s.filesRun && s.filesRun.head === head) return s;
    fx.push(task('files', (signal) => world.files(signal), (o) => ({ t: 'files-loaded', o })));
    return { ...s, filesRun: { head }, filesListed: null, files: reading(s.files) };
  };

  const startDrafts = (s: PrLoads, fx: Fx): PrLoads => {
    if (serverOff(s)) return s;
    fx.push(task('drafts', (signal) => world.drafts(signal), (o) => ({ t: 'drafts-loaded', o })));
    // Why they could not be read stays said until a read succeeds.
    return { ...s, draftsRun: true, draftsStale: false, drafts: s.drafts.tag === 'NotAsked' ? loading : s.drafts };
  };

  const startOverrides = (s: PrLoads, fx: Fx): PrLoads => {
    if (serverOff(s)) return s;
    const basis = s.overridesReads + 1;
    fx.push(task('overrides', (signal) => world.overrides(signal), (o) => ({ t: 'overrides-loaded', basis, o })));
    return { ...s, overridesReads: basis, overrides: reading(s.overrides) };
  };

  /** Work the attention set out from what the server sent; `owed` is confirmed once it is (with what a superseded run owed). */
  const startAttention = (s: PrLoads, fx: Fx, owed: readonly Owed[] = []): PrLoads => {
    const run = s.attentionRuns + 1;
    const { server, timelineTruncated } = s;
    fx.push(task('attention', () => world.attention(server, timelineTruncated), (o) => ({ t: 'attention-done', run, o })));
    return { ...s, attentionRuns: run, attentionOwed: [...s.attentionOwed, ...owed] };
  };

  const startChecks = (s: PrLoads, fx: Fx): PrLoads => {
    if (!s.server.pr || s.revoked) return s;
    const basis = s.reads + 1;
    fx.push(task('checks', (signal) => world.checks(signal), (o) => ({ t: 'checks-loaded', basis, o })));
    return { ...s, reads: basis, checksRun: { basis } };
  };

  const checksSoon = (s: PrLoads, fx: Fx): PrLoads => {
    if (s.checksArmed || s.revoked) return s;
    fx.push(after<Msg>('checks soon', CHECKS_COALESCE_MS, { t: 'checks-fire' }));
    return { ...s, checksArmed: true };
  };

  const refreshSoon = (s: PrLoads, fx: Fx, files: boolean): PrLoads => {
    if (s.revoked) return s;
    fx.push(after<Msg>('refresh', REFRESH_DELAY_MS, { t: 'refresh-fire', files }));
    return s;
  };

  /**
   * Fetch the annotations of completed runs not fetched yet (or completed
   * again since: a re-run); one fetch at a time, and one more after it
   * when asked meanwhile. A failed fetch is not kept, so the next asks again.
   */
  const startAnnotations = (s: PrLoads, fx: Fx): PrLoads => {
    if (s.annotationsRun) return { ...s, annotationsAgain: true };
    const runs = completedRuns(s);
    const listed = new Set(runs.map((r) => r.databaseId!));
    const kept = new Map([...s.annotationRuns].filter(([id]) => listed.has(id)));
    const gone = kept.size !== s.annotationRuns.size;
    const todo = runs.filter((r) => kept.get(r.databaseId!)?.completedAt !== (r.completedAt ?? null) || !kept.has(r.databaseId!));
    const next = gone ? { ...s, annotationRuns: kept, annotations: byFile(runs, kept) } : s;
    if (!todo.length) return next;
    fx.push(
      task<[number, AnnotationRun | null][], Msg>(
        'annotations',
        (signal) =>
          Promise.all(
            todo.map(async (run): Promise<[number, AnnotationRun | null]> => {
              try {
                return [run.databaseId!, { completedAt: run.completedAt ?? null, list: await world.annotations(run, signal) }];
              } catch {
                /* annotations are optional; asked again by the next load */
                return [run.databaseId!, null];
              }
            }),
          ),
        (o) => ({ t: 'annotations-loaded', runs, got: o.ok ? o.value : [] }),
      ),
    );
    return { ...next, annotationsRun: true };
  };

  /** Read the stack from GitHub: what it shows stays meanwhile, and a newer read supersedes one in flight. */
  const startStack = (s: PrLoads, fx: Fx): PrLoads => {
    if (s.revoked) return s;
    fx.push(task('stack', (signal) => world.stackFetch(signal), (o): Msg => ({ t: 'stack-loaded', o })));
    return { ...s, stackAsked: true, stackRun: true, stack: reading(s.stack) };
  };

  /** Ask for the stack, once per page: first what the app already knows of it. */
  const askStack = (s: PrLoads, fx: Fx): PrLoads => {
    if (s.stackAsked || s.revoked) return s;
    fx.push(task('stack', () => world.stackKept(), (o): Msg => ({ t: 'stack-kept', o })));
    return { ...s, stackAsked: true, stackRun: true, stack: loading };
  };

  /** Whether a read at `since` is older than the bound (or claims a time yet to come). */
  const old = (since: number) => since > world.now() || world.now() - since >= STACK_STALE_MS;

  /**
   * After a load of the pull request: read its stack again only when its
   * own branches or state changed, the stack is older than the bound, or
   * its read met a rate limit (this load is the refetch after it).
   */
  const stackAfterLoad = (s: PrLoads, fx: Fx): PrLoads => {
    const pr = s.server.pr;
    if (!pr) return s;
    const own = `${pr.baseRefName}\n${pr.headRefName}\n${pr.state}`;
    const changed = s.stackOwn !== null && s.stackOwn !== own;
    const next: PrLoads = s.stackOwn === own ? s : { ...s, stackOwn: own };
    if (!s.stackAsked) return askStack(next, fx);
    if (s.stackRun && !changed) return next;
    const limited = failureIn(s.stack)?.reason.cause === 'rate_limited';
    return changed || limited || (s.stackAt !== 0 && old(s.stackAt)) ? startStack(next, fx) : next;
  };

  const startAccess = (s: PrLoads, fx: Fx): PrLoads => {
    const repository = s.repository;
    if (!repository) return s;
    const run = s.accessRuns + 1;
    fx.push(task('access', (signal) => world.access(repository, signal), (o) => ({ t: 'access-checked', run, o })));
    return { ...s, accessRuns: run };
  };

  /** What is shown beside the pull request, read after each of its loads. */
  const sideData = (s0: PrLoads, fx: Fx): PrLoads => {
    let s = s0;
    if (!s.sideLoaded) {
      const head = s.server.pr!.headRefOid;
      fx.push(task('marks', () => world.marks(), (): Msg => ({ t: 'done' })));
      fx.push(task('generated', (signal) => world.generated(head, signal), (o): Msg => ({ t: 'generated', o })));
      s = startDrafts({ ...s, sideLoaded: true }, fx);
    }
    // Asked again on later loads while unknown (a transient failure is retried).
    if (s.access === 'unknown') s = startAccess(s, fx);
    if (!s.refsRun && !s.refsFinal) {
      fx.push(task('refs', (signal) => world.refs(signal), (o): Msg => ({ t: 'refs-loaded', o })));
      s = { ...s, refsRun: true, refs: reading(s.refs) };
    }
    if (!s.deploymentsRun) {
      const head = s.server.pr?.headRefOid ?? null;
      fx.push(task('deployments', (signal) => world.deployments(head, signal), (o): Msg => ({ t: 'deployments-loaded', o })));
      s = { ...s, deploymentsRun: true, deployments: reading(s.deployments) };
    }
    const orgReady = s.org.tag === 'Has' && s.org.value.status === 'ready';
    if (!orgReady && !s.orgRun && s.repository) {
      if (s.repository.owner.__typename !== 'Organization') s = { ...s, org: has<OrgPolicy>({ status: 'ready', settings: PERSONAL_POLICY }) };
      else {
        fx.push(task('org', (signal) => world.org(signal), (o): Msg => ({ t: 'org-loaded', o })));
        s = { ...s, orgRun: true, org: s.org.tag === 'NotAsked' ? loading : s.org };
      }
    }
    s = stackAfterLoad(s, fx);
    // Reading the overrides also works the attention set out again.
    s = startOverrides(s, fx);
    return startAnnotations(s, fx);
  };

  /** Server data turns off: what the server kept is dropped, never read again, and attention is worked out without it. */
  const notInstalled = (s: PrLoads, fx: Fx): PrLoads => {
    if (s.revoked) return s;
    fx.push(cancel('overrides'), cancel('drafts'));
    const next: PrLoads = {
      ...s,
      unrecorded: true,
      server: { ...s.server, overrides: [], drafts: [] },
      draftsRun: false,
      draftsStale: false,
      overridesDone: s.overridesReads,
      out: [{ kind: 'forget' }],
    };
    return startAttention(next, fx);
  };

  const invalidated = (s: PrLoads): PrLoads => (s.draftsRun ? { ...s, draftsStale: true } : s);

  return (state, m) => {
    const fx: Fx = [];
    const s: PrLoads = state.out === NO_OUT ? state : { ...state, out: NO_OUT };
    const done = (next: PrLoads): Next<PrLoads, Msg> => [next, fx];
    switch (m.t) {
      case 'done':
        return done(s);

      case 'set':
        return done({ ...s, ...m.patch });

      case 'load':
        // The stack is asked for with the page: what is known of it is there before the pull request is.
        return done(askStack(startLoad(s, fx, m.follow ?? null), fx));

      case 'refresh':
        return done(refreshSoon(s, fx, m.files));

      case 'refresh-fire':
        // Files follow the pull request, so their head is never older than its own.
        return done(startLoad(s, fx, m.files ? 'files' : 'if-loaded'));

      case 'pr': {
        const run = s.prRun;
        const follow = run?.follow ?? null;
        const base: PrLoads = { ...s, prRun: null, prProgress: null, prDone: Math.max(s.prDone, m.basis) };
        // After a refresh the files are read again; after a head move too, unless those on screen are already the new head's.
        const filesNext = (x: PrLoads, moved: boolean) => {
          const loaded = x.files.tag === 'Has' ? x.files.value : null;
          const stale = moved && loaded !== null && loaded.head !== x.server.pr?.headRefOid;
          return follow === 'files' || (follow === 'if-loaded' && loaded !== null) || stale ? startFiles(x, fx, true) : x;
        };
        if (!m.o.ok) {
          const reason = failureOf(m.o.error);
          let next: PrLoads = { ...base, pr: unread(s.pr, reason, retryFor(s, reason), s.lastLoadAt, shown), out: [{ kind: 'stall', resource: 'pr', basis: m.basis }] };
          if (m.o.error instanceof RateLimitError) next = pause(next, fx, false, m.o.error.limit.until);
          return done(filesNext(next, false));
        }
        if (!m.o.value) return done(filesNext({ ...base, pr: has(null), out: [{ kind: 'settle', resource: 'pr', basis: m.basis }] }, false));
        const before = s.server.pr;
        let pr = m.o.value.pr;
        const moved = !!before && before.headRefOid !== pr.headRefOid;
        // Checks refreshed after this load began are newer than the ones it read.
        if (m.basis < s.checksBasis && !moved && before) pr = { ...pr, commits: before.commits };
        const viewed = m.o.value.viewed;
        let viewedPagesHead = s.viewedPagesHead;
        // For a head not seen before, the pages after the first are read in the background; later loads at it keep them.
        if (viewed?.more && viewedPagesHead !== viewed.head) {
          const { more, head } = viewed;
          viewedPagesHead = head;
          fx.push(task('viewed', (signal) => world.viewedPages(more, head, signal), (o): Msg => ({ t: 'viewed-pages', head, complete: o.ok && o.value })));
        }
        const next: PrLoads = {
          ...base,
          viewedPagesHead,
          viewer: m.o.value.viewer,
          repository: m.o.value.repository,
          server: { ...s.server, pr, threads: m.o.value.threads, timeline: m.o.value.timeline },
          timelineTruncated: m.o.value.truncated,
          pr: has(m.o.value),
          shownBasis: m.basis,
          lastLoadAt: world.now(),
          out: [{ kind: 'settle', resource: 'pr', basis: m.basis }, ...(viewed ? [{ kind: 'viewed', viewed } as const] : [])],
        };
        return done(sideData(filesNext(next, moved), fx));
      }

      case 'files':
        return done(startFiles(s, fx, m.force));

      case 'pr-progress':
        return done(s.prRun ? { ...s, prProgress: m.progress } : s);

      case 'files-progress':
        return done(s.filesRun ? { ...s, filesListed: m.listed } : s);

      case 'files-loaded': {
        const base: PrLoads = { ...s, filesRun: null, filesListed: null };
        if (m.o.ok) {
          const next: PrLoads = { ...base, files: has(m.o.value) };
          const shownHead = s.server.pr?.headRefOid ?? null;
          // GitHub listed another head's files than the one on screen (a push the page has not heard of): read the pull request again.
          const behind = m.o.value.head !== null && shownHead !== null && m.o.value.head !== shownHead && !s.prRun;
          return done(behind ? startLoad(next, fx, null) : next);
        }
        const reason = failureOf(m.o.error);
        return done({ ...base, files: unread(s.files, reason, manual, world.now()) });
      }

      case 'drafts':
        return done(startDrafts(s, fx));

      case 'drafts-loaded': {
        if (serverOff(s)) return done({ ...s, draftsRun: false });
        // A write changed the drafts while this read ran: it may predate the write.
        if (s.draftsStale) return done(startDrafts(s, fx));
        const base: PrLoads = { ...s, draftsRun: false };
        if (m.o.ok) return done({ ...base, server: { ...s.server, drafts: m.o.value }, drafts: has(m.o.value) });
        if (isNotInstalled(m.o.error)) return done(notInstalled(base, fx));
        console.warn('drafts unavailable', m.o.error);
        return done({ ...base, drafts: unread(s.drafts, failureOf(m.o.error), manual, world.now()) });
      }

      case 'draft-landed': {
        const d = m.draft;
        // Server data turned off while it was sent: the drafts are not shown, nor kept.
        if (!serverData(s)) return done(s);
        // Dropped since the write started: its answer would bring a discarded draft back.
        if ((s.droppedAt.get(d.id) ?? -1) > m.since) return done(s);
        const held = s.server.drafts.find((x) => x.id === d.id);
        // Answers can arrive out of the server's order: one older than the copy held is ignored.
        if (held && stampOrder(d.updated_at, held.updated_at) < 0) return done(s);
        const drafts = [...s.server.drafts.filter((x) => x.id !== d.id), d].sort((a, b) => Number(a.id) - Number(b.id));
        return done(invalidated({ ...s, draftWrites: s.draftWrites + 1, server: { ...s.server, drafts } }));
      }

      case 'drafts-dropped': {
        const drops = s.drops + 1;
        const droppedAt = new Map(s.droppedAt);
        for (const id of m.ids) droppedAt.set(id, drops);
        return done(
          invalidated({ ...s, drops, droppedAt, draftWrites: s.draftWrites + 1, server: { ...s.server, drafts: s.server.drafts.filter((d) => !m.ids.includes(d.id)) } }),
        );
      }

      case 'drafts-mirrored':
        if (!serverData(s)) return done(s);
        // A write was answered while the mirror ran: its list may predate the write (a draft just
        // saved missing from it, a discarded one still in it). The write's result stands, and the
        // server is asked what it holds now, mirror and write both done.
        if (s.draftWrites !== m.since) return done(startDrafts(s, fx));
        // The drafts as the server holds them now; the pending review on GitHub is as it was.
        return done(invalidated({ ...s, server: { ...s.server, drafts: m.drafts } }));

      case 'overrides':
        return done(startOverrides(s, fx));

      case 'overrides-loaded': {
        if (serverOff(s)) return done(s);
        if (m.o.ok)
          return done(startAttention({ ...s, server: { ...s.server, overrides: m.o.value }, overrides: has(m.o.value) }, fx, [{ basis: m.basis, ok: true }]));
        if (isNotInstalled(m.o.error)) return done(notInstalled(s, fx));
        console.warn('attention overrides unavailable', m.o.error);
        // The attention set is worked out with the overrides the server sent before.
        return done(startAttention({ ...s, overrides: unread(s.overrides, failureOf(m.o.error), manual, world.now()) }, fx, [{ basis: m.basis, ok: false }]));
      }

      case 'attention':
        return done(startAttention(s, fx));

      case 'attention-done': {
        if (!m.o.ok) console.warn('attention failed', m.o.error);
        const members = m.o.ok ? m.o.value : null;
        const owed = s.attentionOwed;
        return done({
          ...s,
          server: members ? { ...s.server, attention: members } : s.server,
          attentionDone: Math.max(s.attentionDone, m.run),
          attentionOwed: [],
          overridesDone: owed.reduce((n, c) => Math.max(n, c.basis), s.overridesDone),
          // Only the changes accepted before each read began are in it (one accepted meanwhile stays shown).
          out: owed.length ? owed.map((c): Out => ({ kind: c.ok ? 'settle' : 'stall', resource: 'overrides', basis: c.basis })) : NO_OUT,
        });
      }

      case 'checks':
        return done(startChecks(s, fx));

      case 'checks-soon':
        return done(checksSoon(s, fx));

      case 'checks-fire': {
        const idle: PrLoads = { ...s, checksArmed: false };
        // One more refetch after the one running, for every notice that arrived during it.
        return done(s.checksRun ? { ...idle, checksAgain: true } : startChecks(idle, fx));
      }

      case 'checks-loaded': {
        let next: PrLoads = { ...s, checksRun: null, checksDone: Math.max(s.checksDone, m.basis), checksAgain: false };
        const now = s.server.pr;
        if (!m.o.ok) {
          if (m.o.error instanceof RateLimitError) next = pause(next, fx, false, m.o.error.limit.until);
          else console.warn('checks refresh failed', m.o.error);
        } else if (!m.o.value || !now || m.o.value.head !== now.headRefOid) {
          // The head moved (or the pull request is gone): the whole of it is read again.
          next = refreshSoon(next, fx, true);
        } else if (m.basis > s.shownBasis) {
          // Not when a load begun after this refresh is on screen: its checks are newer.
          next = startAnnotations({ ...next, server: { ...s.server, pr: { ...now, commits: m.o.value.commits } }, checksBasis: Math.max(s.checksBasis, m.basis) }, fx);
        }
        return done(s.checksAgain ? checksSoon(next, fx) : next);
      }

      case 'org-loaded': {
        if (m.o.ok) return done({ ...s, orgRun: false, org: has(m.o.value) });
        return done({ ...s, orgRun: false, org: failed({ ...failureOf(m.o.error), message: message(m.o.error) }, manual) });
      }

      case 'refs-loaded': {
        const base: PrLoads = { ...s, refsRun: false };
        const kept = (): PrLoads => ({ ...base, refs: s.refs.tag === 'Loading' ? notAsked : s.refs });
        if (!m.o.ok || m.o.value.kind === 'kept') return done(kept());
        if (m.o.value.kind === 'read') return done({ ...base, refs: has(m.o.value.refs) });
        // Failed for now: the next load asks again; a query the server cannot answer, never.
        return done({ ...kept(), refsFinal: !m.o.value.retry });
      }

      case 'deployments-loaded': {
        const base: PrLoads = { ...s, deploymentsRun: false };
        if (m.o.ok && Array.isArray(m.o.value)) return done({ ...base, deployments: has(m.o.value) });
        if (!m.o.ok) console.warn('deployments unavailable', m.o.error);
        // What was read before stays; the next load tries again.
        return done({ ...base, deployments: s.deployments.tag === 'Loading' ? notAsked : s.deployments });
      }

      case 'stack':
        return done(startStack(s, fx));

      case 'stack-kept': {
        const idle: PrLoads = { ...s, stackRun: false };
        const kept = m.o.ok ? m.o.value : null;
        // Nothing known, or only that it was on its own a while ago: GitHub says.
        if (!kept || (kept.kind === 'alone' && old(kept.since))) return done(startStack(idle, fx));
        if (kept.kind === 'alone') return done({ ...idle, stack: has(null), stackAt: kept.since });
        if (!old(kept.since)) return done({ ...idle, stack: has(kept.pool), stackAt: kept.since });
        // Shown at once as of then, and checked once in the background.
        return done(startStack({ ...idle, stack: stale(kept.pool, kept.since), stackAt: kept.since }, fx));
      }

      case 'stack-loaded': {
        const idle: PrLoads = { ...s, stackRun: false };
        if (m.o.ok) return done({ ...idle, stack: has(m.o.value), stackAt: world.now() });
        const reason = failureOf(m.o.error);
        // What was read before stays, as of then; its age is not asked about again until the user does.
        let next: PrLoads = { ...idle, stack: unread(s.stack, reason, manual, s.stackAt || world.now()), stackAt: 0 };
        // The refetch armed for the limit's end reads the pull request, and its stack after it.
        if (m.o.error instanceof RateLimitError) next = pause(next, fx, false, m.o.error.limit.until);
        return done(next);
      }

      case 'replaced': {
        const pr = s.server.pr;
        if (!pr) return done(s);
        const befores = forcePushBefores(s.server.timeline);
        if (!befores.length) return done(s);
        const key = `${pr.headRefOid}:${befores.join(',')}`;
        if (key === s.replacedKey) return done(s);
        const base = pr.baseRefOid;
        fx.push(task('replaced', (signal) => world.replaced(base, befores, signal), (o): Msg => ({ t: 'replaced-loaded', key, o })));
        return done({ ...s, replacedKey: key });
      }

      case 'replaced-loaded':
        if (m.key !== s.replacedKey) return done(s);
        // A failure leaves what the timeline shows, and is looked up again when next asked.
        return done(m.o.ok ? { ...s, replaced: m.o.value } : { ...s, replacedKey: '' });

      case 'annotations':
        return done(startAnnotations(s, fx));

      case 'annotations-loaded': {
        const fetched = new Map(s.annotationRuns);
        for (const [id, run] of m.got) if (run) fetched.set(id, run);
        const next: PrLoads = { ...s, annotationRuns: fetched, annotations: byFile(m.runs, fetched), annotationsRun: false, annotationsAgain: false };
        return done(s.annotationsAgain ? startAnnotations(next, fx) : next);
      }

      case 'access':
        return done(startAccess(s, fx));

      case 'access-checked': {
        const base: PrLoads = { ...s, accessDone: Math.max(s.accessDone, m.run) };
        // 'unknown' keeps what a failed write may already have shown, and so does any
        // answer once a write has failed for the App's access while this one was on its way.
        if (!m.o.ok || m.o.value === 'unknown' || s.access === 'not_installed') return done(base);
        return done({ ...base, access: m.o.value });
      }

      case 'generated':
        return done(m.o.ok ? { ...s, generated: m.o.value } : s);

      case 'viewed-pages':
        // Cut short (a failure, a push): the next load reads them again.
        return done(!m.complete && s.viewedPagesHead === m.head ? { ...s, viewedPagesHead: null } : s);

      case 'paused':
        return done(pause(s, fx, m.files, m.until));

      case 'resume-armed':
        if (s.resume !== 'arming') return done(s);
        return done({ ...s, resume: m.s, pr: adopt(s.pr, m.s) });

      case 'resume-lapsed':
        return done({ ...s, resume: s.resume === m.s ? null : s.resume, pr: settle(s.pr, m.s) });

      case 'resume-fire': {
        if (s.resume !== m.s) return done(s);
        const files = s.resumeFiles;
        const idle: PrLoads = { ...s, resume: null, resumeFiles: false };
        // The pause was extended meanwhile: wait for its new end.
        if ((world.pausedUntil() ?? -Infinity) > world.now()) return done(pause(idle, fx, files, null));
        return done(refreshSoon(idle, fx, files));
      }

      case 'write': {
        const write = s.writes + 1;
        const { kind, send, settle } = m;
        // A lane of its own: no load and no other write supersedes it. Logged as an intent before it goes, done whatever came of it.
        fx.push(
          task(
            `write ${write}`,
            async () => {
              const answered = world.writing(kind);
              try {
                return await send();
              } finally {
                answered();
              }
            },
            (o): Msg => ({ t: 'write-answered', write, o, settle }),
          ),
        );
        return done({ ...s, writes: write, sending: s.sending + 1 });
      }

      case 'write-answered':
        return done({ ...s, sending: Math.max(0, s.sending - 1), out: [{ kind: 'answered', write: m.write, o: m.o, settle: m.settle }] });

      case 'not-installed':
        return done(notInstalled(s, fx));

      case 'revoked':
        for (const lane of LANES) fx.push(cancel(lane));
        return done({
          ...initialState(),
          revoked: true,
          access: s.access,
          unrecorded: s.unrecorded,
          reads: s.reads,
          prDone: s.reads,
          checksDone: s.reads,
          overridesReads: s.overridesReads,
          overridesDone: s.overridesReads,
          attentionRuns: s.attentionRuns,
          attentionDone: s.attentionRuns,
          accessRuns: s.accessRuns,
          accessDone: s.accessRuns,
          // Writes in flight are still answered.
          writes: s.writes,
          sending: s.sending,
          // Nothing reloads any more: writes the server accepted are done.
          out: [{ kind: 'forget' }, { kind: 'settle', resource: 'pr', basis: Infinity }],
        });
    }
  };
}

const LANES = [
  'pr',
  'checks',
  'files',
  'drafts',
  'overrides',
  'attention',
  'org',
  'refs',
  'deployments',
  'stack',
  'replaced',
  'annotations',
  'access',
  'generated',
  'marks',
  'viewed',
  'refresh',
  'checks soon',
  'resume',
] as const;
